import { ChangeDetectionStrategy, Component, EventEmitter, Input, NgModule, Output, ViewEncapsulation } from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';
import { FormControl } from '@angular/forms';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { combineLatest, map, Observable, tap } from 'rxjs';
import { CommonModule } from '@angular/common';
import { ObservableInput, ReduxModule } from '../../../../utils/util.redux';
import { DateUtility } from '@jms/model';
import { LuxonCalendarModule } from '@workbuddy/ui/fields/luxon.calendar';
import { StateComponent } from '@jms/utils/util.state.component';
import { DateTime } from 'luxon';

interface WeekRangePickerComponentVM {
  date: Date;
  dateFormat: string;
  timezone: string;
  monday: DateTime;
  sunday: DateTime;
}

enum MoveType {
  Back = 'Back',
  Forward = 'Forward',
  Today = 'Today',
  ToDate = 'To Date'
}

const initialState = {
  date: new Date(),
  monday: null,
  sunday: null,
  dateFormat: 'dd/MM/yyyy',
  timezone: Intl.DateTimeFormat().resolvedOptions().timeZone
}

@UntilDestroy()
@Component({
  selector: 'week-range-picker',
  templateUrl: './week.range.picker.component.html',
  styleUrls: ['./week.range.picker.component.less'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    ReduxModule,
    ReactiveFormsModule,
    LuxonCalendarModule
  ],
})
export class WeekRangePickerComponent extends StateComponent<WeekRangePickerComponentVM> {

  @ObservableInput() @Input('date') public date$: Observable<Date>;
  @ObservableInput() @Input('dateFormat') public dateFormat$: Observable<string>;
  @ObservableInput() @Input('timezone') public timezone$: Observable<string>;
  @Input() public classCss: string;
  @Input() public showLabel: boolean;

  @Output() public change = new EventEmitter<DateTime>();

  public selectedDate = new FormControl();
  public MoveType = MoveType;

  public styleIconCalendar = {
    'background': 'transparent',
    'color': '#111',
    'font-size': '12px',
    'border': 'none'
  };

  constructor() {

    super(initialState);

    this.styleIconCalendar = {
      'background': 'transparent',
      'color': '#111',
      'font-size': '12px',
      'border': 'none'
    };



    combineLatest([
      this.date$,
      this.timezone$,
      this.dateFormat$
    ])
      .pipe(
        map(([date, timezone, dateFormat]) => {

          if (!date)
            return;

          const monday = DateUtility.getDateTime(date, timezone).startOf('week');
          const sunday = monday.plus({ 'day': 6 });

          this.setStateDirect({
            date: date,
            timezone: timezone,
            dateFormat: dateFormat,
            monday: monday,
            sunday: sunday
          });

        })
      ).subscribe()

    this.selectedDate.valueChanges
      .pipe(
        untilDestroyed(this),
        tap(x => {

          const date = DateUtility.getDateTime(new Date(x));
          this.change.emit(date)
        })
      ).subscribe();

  }

  public onChangeDate(type: MoveType) {

    switch (type) {
      case MoveType.Back: {
        const lastWeek = this.vm.monday.minus({ day: 6 });
        this.change.emit(lastWeek);
        break;
      }
      case MoveType.Forward: {
        const nextWeek = this.vm.sunday.plus({ day: 6 });
        this.change.emit(nextWeek);
        break;
      }
      case MoveType.Today: {
        const today = DateUtility.getNowAsUtcWithCurrentTime();
        this.change.emit(today);
        break;
      }
    }

  }


}

@NgModule({
  imports: [
    WeekRangePickerComponent
  ],
  exports: [
    WeekRangePickerComponent
  ],
})
export class WeekRangePickerModule { }
